/**
 * CompetitorsPage — live competitor price intelligence.
 *
 * Left panel: SSE-powered scanner (MPN → live log → prices table).
 * Right panel: Recent scans table from /api/curated, sorted by spread desc.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ColumnDef,
  SortingState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  ExternalLink,
  Loader2,
  Radar,
  Search,
  TriangleAlert,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { CuratedProduct, api } from "@/lib/api";
import { formatUsd } from "@/lib/utils";

// ── SSE types ────────────────────────────────────────────────────────────────

interface SsePrice {
  name: string;
  price: number;
  url: string;
}

type ScanStatus = "idle" | "scanning" | "done" | "error";

// ── Recent-scans table row ────────────────────────────────────────────────────

interface RecentRow {
  id: string;
  title: string;
  tdsynnex_sku: string;
  count: number;
  lowest: number | null;
  ourPrice: number | null;
  spread: number | null; // ourPrice - lowest (positive = we're cheaper, negative = undercut)
  mpn: string | null;
}

function buildRecentRows(items: CuratedProduct[]): RecentRow[] {
  return items
    .filter((p) => p.competitor_prices && p.competitor_prices.length > 0)
    .map((p) => {
      const prices = p.competitor_prices.map((c) => c.price).filter((x) => x > 0);
      const lowest = prices.length > 0 ? Math.min(...prices) : null;
      const ourPrice = p.proposed_price ? parseFloat(p.proposed_price) : null;
      const spread =
        ourPrice !== null && lowest !== null ? ourPrice - lowest : null;
      return {
        id: p.id,
        title: p.title ?? p.tdsynnex_sku,
        tdsynnex_sku: p.tdsynnex_sku,
        count: p.competitor_prices.length,
        lowest,
        ourPrice,
        spread,
        mpn: null, // not in CuratedProduct; prefill via SKU fallback
      };
    })
    .sort((a, b) => {
      // Biggest positive spread first (we're priced above cheapest competitor)
      const sa = a.spread ?? -Infinity;
      const sb = b.spread ?? -Infinity;
      return sb - sa;
    });
}

// ── Scanner panel ─────────────────────────────────────────────────────────────

function ScannerPanel({
  initialMpn,
  onReset,
}: {
  initialMpn: string;
  onReset: () => void;
}) {
  const [mpn, setMpn] = useState(initialMpn);
  const [brand, setBrand] = useState("");
  const [description, setDescription] = useState("");

  const [status, setStatus] = useState<ScanStatus>("idle");
  const [logs, setLogs] = useState<string[]>([]);
  const [prices, setPrices] = useState<SsePrice[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const logRef = useRef<HTMLDivElement>(null);
  const esRef = useRef<EventSource | null>(null);

  // Sync initialMpn → input when a row is clicked
  useEffect(() => {
    setMpn(initialMpn);
  }, [initialMpn]);

  // Auto-scroll log to bottom
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logs]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      esRef.current?.close();
    };
  }, []);

  function startScan() {
    if (!mpn.trim()) return;

    // Close any previous stream
    esRef.current?.close();

    setStatus("scanning");
    setLogs([]);
    setPrices([]);
    setErrorMsg(null);

    const qs = new URLSearchParams({ mpn: mpn.trim() });
    if (brand.trim()) qs.set("brand", brand.trim());
    if (description.trim()) qs.set("description", description.trim());

    const es = new EventSource(`/api/competitors/scan/stream?${qs.toString()}`);
    esRef.current = es;

    es.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data) as {
          type: "step" | "prices" | "done" | "error";
          text?: string;
          prices?: SsePrice[];
          error?: string;
        };

        if (data.type === "step" && data.text) {
          setLogs((prev) => [...prev, data.text!]);
        } else if (data.type === "prices" && data.prices) {
          setPrices(data.prices);
        } else if (data.type === "done") {
          setStatus("done");
          es.close();
        } else if (data.type === "error") {
          setErrorMsg(data.error ?? "Unknown error");
          setStatus("error");
          es.close();
        }
      } catch {
        // malformed event — skip
      }
    };

    es.onerror = () => {
      if (status !== "done") {
        setErrorMsg("Stream connection lost.");
        setStatus("error");
      }
      es.close();
    };
  }

  function handleReset() {
    esRef.current?.close();
    setStatus("idle");
    setLogs([]);
    setPrices([]);
    setErrorMsg(null);
    setMpn("");
    setBrand("");
    setDescription("");
    onReset();
  }

  const scanning = status === "scanning";

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Radar className="h-4 w-4 text-ember" />
          Scanner
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {/* Form */}
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              MPN <span className="text-rose-500">*</span>
            </span>
            <Input
              placeholder="e.g. 210-AXWN"
              value={mpn}
              onChange={(e) => setMpn(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && startScan()}
              disabled={scanning}
              className="font-mono"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Brand
              </span>
              <Input
                placeholder="e.g. Dell"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                disabled={scanning}
              />
            </div>
            <div className="grid gap-1.5">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Description
              </span>
              <Input
                placeholder="optional hint"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={scanning}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={startScan}
              disabled={scanning || !mpn.trim()}
              className="flex-1"
            >
              {scanning ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Scanning…
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Scan
                </>
              )}
            </Button>
            {(status !== "idle" || mpn) && (
              <Button variant="outline" onClick={handleReset} disabled={scanning}>
                Reset
              </Button>
            )}
          </div>
        </div>

        {/* Live log */}
        {(logs.length > 0 || scanning) && (
          <div
            ref={logRef}
            className="h-36 overflow-y-auto rounded-md border border-border bg-black/60 p-3 font-mono text-xs leading-relaxed text-emerald-400"
          >
            {logs.map((line, i) => (
              <div key={i}>{line}</div>
            ))}
            {scanning && (
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" />
                <span>waiting…</span>
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {status === "error" && errorMsg && (
          <div className="flex items-start gap-2 rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2.5 text-xs text-rose-400">
            <TriangleAlert className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            {errorMsg}
          </div>
        )}

        {/* Results table */}
        {status === "done" && (
          <div>
            {prices.length === 0 ? (
              <p className="text-center text-xs text-muted-foreground py-4">
                No prices found for this MPN.
              </p>
            ) : (
              <>
                <div className="text-xs text-muted-foreground mb-2">
                  {prices.length} result{prices.length !== 1 ? "s" : ""} for{" "}
                  <span className="font-mono text-foreground">{mpn}</span>
                </div>
                <div className="rounded-md border border-border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Retailer</TableHead>
                        <TableHead className="text-xs text-right">Price</TableHead>
                        <TableHead className="text-xs w-10" />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {prices
                        .slice()
                        .sort((a, b) => a.price - b.price)
                        .map((p, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-sm">{p.name}</TableCell>
                            <TableCell className="text-sm text-right font-mono tabular-nums">
                              {formatUsd(p.price)}
                            </TableCell>
                            <TableCell className="text-right">
                              {p.url && (
                                <a
                                  href={p.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center text-muted-foreground hover:text-foreground"
                                >
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </a>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Recent scans table ────────────────────────────────────────────────────────

const recentColumns: ColumnDef<RecentRow>[] = [
  {
    accessorKey: "title",
    header: "Product",
    cell: ({ row }) => (
      <div className="max-w-[220px]">
        <div className="truncate text-sm font-medium">{row.original.title}</div>
        <div className="font-mono text-[10px] text-muted-foreground">
          {row.original.tdsynnex_sku}
        </div>
      </div>
    ),
  },
  {
    accessorKey: "count",
    header: "# Prices",
    cell: ({ getValue }) => (
      <Badge variant="secondary" className="tabular-nums">
        {getValue() as number}
      </Badge>
    ),
  },
  {
    accessorKey: "lowest",
    header: "Lowest",
    cell: ({ getValue }) => {
      const v = getValue() as number | null;
      return (
        <span className="font-mono tabular-nums text-sm text-rose-400">
          {v !== null ? formatUsd(v) : "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "ourPrice",
    header: "Our Price",
    cell: ({ getValue }) => {
      const v = getValue() as number | null;
      return (
        <span className="font-mono tabular-nums text-sm">
          {v !== null ? formatUsd(v) : "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "spread",
    header: "Spread",
    sortDescFirst: true,
    cell: ({ getValue }) => {
      const v = getValue() as number | null;
      if (v === null) return <span className="text-muted-foreground">—</span>;
      const isUndercut = v < 0;
      return (
        <span
          className={`font-mono tabular-nums text-sm font-semibold ${
            isUndercut ? "text-rose-400" : "text-emerald-400"
          }`}
        >
          {v > 0 ? "+" : ""}
          {formatUsd(v)}
        </span>
      );
    },
  },
];

function RecentScansTable({
  onRowClick,
}: {
  onRowClick: (row: RecentRow) => void;
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["curated-with-competitors"],
    queryFn: () => api.curatedList({ status: "" }),
    staleTime: 60_000,
  });

  const rows = useMemo(
    () => buildRecentRows(data?.items ?? []),
    [data],
  );

  const [sorting, setSorting] = useState<SortingState>([
    { id: "spread", desc: true },
  ]);

  const table = useReactTable({
    data: rows,
    columns: recentColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground text-sm gap-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading…
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-rose-500/30 bg-rose-500/10 px-3 py-2.5 text-xs text-rose-400">
        <TriangleAlert className="h-3.5 w-3.5 shrink-0" />
        Failed to load curated products.
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="text-center text-xs text-muted-foreground py-8">
        No products have competitor prices yet. Run a scan above.
      </p>
    );
  }

  return (
    <div className="rounded-md border border-border overflow-hidden">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id}>
              {hg.headers.map((h) => (
                <TableHead
                  key={h.id}
                  className="text-xs cursor-pointer select-none"
                  onClick={h.column.getToggleSortingHandler()}
                >
                  <span className="flex items-center gap-1">
                    {flexRender(h.column.columnDef.header, h.getContext())}
                    {h.column.getCanSort() && (
                      <span className="text-muted-foreground">
                        {h.column.getIsSorted() === "asc"
                          ? "↑"
                          : h.column.getIsSorted() === "desc"
                          ? "↓"
                          : "⇅"}
                      </span>
                    )}
                  </span>
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              className="cursor-pointer hover:bg-muted/40"
              onClick={() => onRowClick(row.original)}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function CompetitorsPage() {
  // MPN pre-fill lifted here so the Recent table can drive the Scanner
  const [prefillMpn, setPrefillMpn] = useState("");
  const [prefillKey, setPrefillKey] = useState(0); // bump to force Scanner re-mount

  function handleRowClick(row: RecentRow) {
    // Use SKU as fallback if no mpn field available
    setPrefillMpn(row.tdsynnex_sku);
    setPrefillKey((k) => k + 1);
    // Scroll to top on mobile
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Competitors</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Live price intelligence — scan any MPN across the web.
        </p>
      </div>

      {/* Two-panel layout */}
      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6 items-start">
        {/* Scanner */}
        <ScannerPanel
          key={prefillKey}
          initialMpn={prefillMpn}
          onReset={() => setPrefillMpn("")}
        />

        {/* Recent scans */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Radar className="h-4 w-4 text-muted-foreground" />
              Recent Scans
              <span className="text-xs font-normal text-muted-foreground ml-1">
                click a row to pre-fill scanner
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <RecentScansTable onRowClick={handleRowClick} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
